import { memo, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";
import cursor from "@/assets/cursor.cur";
import { FRAME_PER_SECOND } from "@/lib/constants";
import { useStoreSynced } from "@/lib/store-synced";
import type { Position } from "@/lib/types";

interface RealtimeCursorProps extends Position {
  podNumber: string;
}

const RealtimeCursor = memo(function RealtimeCursor({
  x,
  y,
  podNumber,
}: RealtimeCursorProps) {
  return (
    <div
      className="pointer-events-none absolute left-0 top-0 transition-all ease-linear"
      style={{
        transform: `translate(${x}px, ${y}px)`,
        transitionDuration: `${1000 / FRAME_PER_SECOND}ms`,
      }}
    >
      <img src={cursor} alt="cursor" className="size-4" />
      <div className="mt-1 whitespace-nowrap rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
        {podNumber}
      </div>
    </div>
  );
});

export const RealtimeCursors = memo(function RealtimeCursors() {
  const { clientStatuses, setClientStatus } = useStoreSynced(
    useShallow((s) => ({
      clientStatuses: s.clientStatuses,
      setClientStatus: s.setClientStatus,
    })),
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setClientStatus((s) => ({
        ...s,
        cursorPosition: { x: e.clientX, y: e.clientY },
      }));
    };
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [setClientStatus]);

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {(() => {
        const withCursor = Object.entries(clientStatuses).filter(
          ([, data]) => data.cursorPosition !== undefined,
        ) as [string, { cursorPosition: Position }][];
        return withCursor.map(([key, data]) => (
          <RealtimeCursor
            key={key}
            x={data.cursorPosition.x}
            y={data.cursorPosition.y}
            podNumber={key}
          />
        ));
      })()}
    </div>
  );
});
